import { DeleteOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, MoreOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { Affix, Button, Card, DatePicker, Dropdown, Empty, InputNumber, Menu, Modal, Popconfirm, Space, Table, Tabs, Upload } from "antd";
import ListTable from "common/ListTable";
import moment from "moment";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BUYER_INQUIRY_DELETE_ENDPOINT, BUYER_INQUIRY_LIST_ENDPOINT, MIGRATE_BUYER_INQUIRY_ENDPOINT } from "../../apiServices/API_ENDPOINTS";
import { deleteData, getData, postData } from "../../apiServices/common";
import CompactHorizontalScroll from "../../common/CompactHorizontalScroll";
import HandleNotification from "../../common/Notification";
import {
  BUYER_INQUIRY_CREATE_PERMISSION,
  BUYER_INQUIRY_DELETE_PERMISSION,
  BUYER_INQUIRY_EDIT_PERMISSION,
  BUYER_INQUIRY_MULTI_STYLE_CREATE_PERMISSION,
  BUYER_INQUIRY_VIEW_PERMISSION,
  CM_VALUE_APPROVE_PERMISSION,
} from "../../routes/permissions";
import { commaSeparateNumber, hasPermission, toFixedAndComma } from "../../utils/functions";
import { isMobileView } from "../Common/AppHelper";
import CustomFilter from "../Common/CustomFilter";
import Header from "../Common/Header";
import AddMultiStyleModal from "./AddOrUpdateBuyerInquiry/AddMultiStyleModal";
import UploadCMDoc from "./AddOrUpdateBuyerInquiry/UploadCMDoc";

const calculateTotalExpectedMargin = (breakevenCm, expectedMargin) => {
  const breakeven = Number(breakevenCm);
  if (breakevenCm === undefined || breakevenCm === null || isNaN(breakeven)) {
    return null;
  }

  const profitMargin = Number(expectedMargin || 0);
  const marginAmount = isNaN(profitMargin) ? 0 : (breakeven * profitMargin) / 100;
  const total = breakeven + marginAmount;
  return isNaN(total) ? null : total;
};

const calculateQuotedCmFromTotalMargin = (totalExpectedMargin) => {
  const total = Number(totalExpectedMargin);
  if (
    totalExpectedMargin === undefined ||
    totalExpectedMargin === null ||
    isNaN(total)
  ) {
    return null;
  }

  return Number(parseFloat(total).toFixed(2));
};

const enrichMultiStyleCMRow = (row) => {
  const expectedMargin =
    row?.expected_margin === 0 ? null : row?.expected_margin ?? null;
  const totalExpectedMargin = calculateTotalExpectedMargin(
    row?.breakeven_cm,
    expectedMargin,
  );

  return {
    ...row,
    expected_margin: expectedMargin,
    total_expected_margin: totalExpectedMargin,
    quoted_cm: calculateQuotedCmFromTotalMargin(totalExpectedMargin),
  };
};

const BuyerInquiry = (props) => {
  // Props
  const { width } = props;

  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const filterStorageKey = "buyer_inquiry_filter_values_by_tab";

  const getPersistedFilters = () => {
    try {
      const raw = localStorage.getItem(filterStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  };

  const persistFilters = (tabKey, filters) => {
    try {
      const persisted = getPersistedFilters();
      persisted[tabKey] = filters;
      localStorage.setItem(filterStorageKey, JSON.stringify(persisted));
    } catch (e) {
      // Ignore persistence failures (private mode, quota issues, etc.)
    }
  };

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [permitList, setPermitList] = useState();
  const [activeTab, setActiveTab] = useState("single_style");
  const [tabWiseFilterValues, setTabWiseFilterValues] = useState({
    single_style: {},
    multi_style: {},
  });
  const [tabWiseCurrentPage, setTabWiseCurrentPage] = useState({
    single_style: 1,
    multi_style: 1,
  });
  const [tabWiseTotalData, setTabWiseTotalData] = useState({
    single_style: 0,
    multi_style: 0,
  });
  const [loading, setLoading] = useState(false);
  const [tabWiseRangeData, setTabWiseRangeData] = useState({
    single_style: 10,
    multi_style: 10,
  });
  const [isMultiStyleCMModalOpen, setIsMultiStyleCMModalOpen] = useState(false);
  const [isMultiStyleCMModalLoading, setIsMultiStyleCMModalLoading] = useState(false);
  const [isMultiStyleCMSaving, setIsMultiStyleCMSaving] = useState(false);
  const [isMultiStyleCMExporting, setIsMultiStyleCMExporting] = useState(false);
  const [selectedMultiStyleInquiryId, setSelectedMultiStyleInquiryId] = useState(null);
  const [multiStyleCMData, setMultiStyleCMData] = useState([]);
  const [multiStyleCMOriginalData, setMultiStyleCMOriginalData] = useState([]);
  const [multiStyleCMFilesList, setMultiStyleCMFilesList] = useState([]);
  const [multiStyleCMFieldsChanged, setMultiStyleCMFieldsChanged] = useState(false);
  const [isAddMultiStyleModalOpen, setIsAddMultiStyleModalOpen] = useState(false);
  const [selectedAddMultiStyleInquiry, setSelectedAddMultiStyleInquiry] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [uploadErrorModal, setUploadErrorModal] = useState({
    visible: false,
    message: "",
    errors: [],
  });

  // ux2(C1): received_date has no default filter today; open on the last 30 days so the page
  // isn't blank, while the endpoint stays backward compatible when from_date/to_date are absent.
  const getDefaultDateRange = () => [moment().subtract(29, "days"), moment()];
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Guard against setState after unmount and against a slower, stale request clobbering a
  // faster, later one (round-1 pattern).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const reqIdRef = useRef(0);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllPermitList = useCallback(
    async (filterValues = undefined, tabKey = activeTab) => {
      const myReq = ++reqIdRef.current;
      setLoading(true);

      const savedFilterValues = tabWiseFilterValues?.[tabKey] || {};
      const activeFilterValues = typeof filterValues === "undefined" ? savedFilterValues : filterValues || {};

      // Persist only when caller provides an explicit filter payload
      if (typeof filterValues !== "undefined") {
        persistFilters(tabKey, activeFilterValues);
      }

      const currentTabPage = activeFilterValues?.page || tabWiseCurrentPage?.[tabKey] || 1;
      const currentTabPerPage = activeFilterValues?.per_page || tabWiseRangeData?.[tabKey] || 10;

      const bodyData = {
        ...activeFilterValues,
        per_page: currentTabPerPage,
        page: currentTabPage,
        // inquiry_status: [0, 1, 2],
        is_multiple_style: tabKey === "multi_style" ? "Yes" : "No",
      };

      // ux2(C1): seed the 30-day default onto every fetch that doesn't already carry its own
      // dates (the advanced Search filter has none today, see SearchForm "buyer_inquiry" case).
      if (!bodyData.from_date && !bodyData.to_date && dateRange?.[0] && dateRange?.[1]) {
        bodyData.from_date = dateRange[0].format("YYYY-MM-DD");
        bodyData.to_date = dateRange[1].format("YYYY-MM-DD");
      }

      const query = `${BUYER_INQUIRY_LIST_ENDPOINT}`;
      let response = await getData(query, false, bodyData);

      // Stale-response guard: drop this response if the component unmounted or a newer
      // request (e.g. the user changing the date range again) has since been fired.
      if (!mountedRef.current || myReq !== reqIdRef.current) return;

      if (response && response?.data?.code === 200) {
        const masterData = response?.data;
        setPermitList(masterData?.data?.data);
        setTabWiseTotalData((prev) => ({
          ...prev,
          [tabKey]: masterData?.data?.total || 0,
        }));
        setTabWiseRangeData((prev) => ({
          ...prev,
          [tabKey]: masterData?.data?.per_page || currentTabPerPage,
        }));
        setTabWiseCurrentPage((prev) => ({
          ...prev,
          [tabKey]: masterData?.data?.current_page || currentTabPage,
        }));
        setTabWiseFilterValues((prev) => ({
          ...prev,
          [tabKey]: {
            ...activeFilterValues,
            page: masterData?.data?.current_page || currentTabPage,
            per_page: masterData?.data?.per_page || currentTabPerPage,
          },
        }));
      } else {
        HandleNotification("error", "bottomRight", "Something Went Wrong!", null);
      }

      setLoading(false);
    },
    [activeTab, tabWiseCurrentPage, tabWiseFilterValues, tabWiseRangeData, dateRange],
  );

  const handleDateRangeChange = (dates) => {
    const nextRange = dates && dates[0] && dates[1] ? dates : getDefaultDateRange();
    setDateRange(nextRange);
    // Re-fetch the active tab immediately with the new range merged onto its current filter
    // (passed explicitly — state set above hasn't landed in this closure yet).
    getAllPermitList(
      {
        ...(tabWiseFilterValues?.[activeTab] || {}),
        page: 1,
        from_date: nextRange[0].format("YYYY-MM-DD"),
        to_date: nextRange[1].format("YYYY-MM-DD"),
      },
      activeTab,
    );
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.toString()) {
      const page = searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1;
      const per_page = searchParams.get("per_page") ? parseInt(searchParams.get("per_page"), 10) : 10;
      const cm_status = searchParams.get("cm_status") !== null ? parseInt(searchParams.get("cm_status"), 10) : undefined;
      const is_multiple_style = searchParams.get("is_multiple_style") ?? "No";
      const ob_status = searchParams.get("ob_status") ?? undefined;

      const tabKey = is_multiple_style === "Yes" ? "multi_style" : "single_style";
      setActiveTab(tabKey);

      const filters = {};
      if (cm_status !== undefined) filters.cm_status = cm_status;
      if (ob_status !== undefined) filters.ob_status = ob_status;
      filters.page = page;
      filters.per_page = per_page;

      getAllPermitList(filters, tabKey);
    } else {
      const persisted = getPersistedFilters();
      const persistedForTab = persisted?.[activeTab] || {};
      const hasPersistedFilters = persistedForTab && Object.keys(persistedForTab).length > 0;
      getAllPermitList(hasPersistedFilters ? persistedForTab : undefined, activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const baseColumns = [
    {
      title: "Inquiry No.",
      dataIndex: ["inquiry_no"],
      key: "inquiry_no",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office_info", "name"],
      key: "buying_office_name",
    },
    {
      title: "Brand",
      dataIndex: ["brand_info", "name"],
      key: "brand_name",
    },
    {
      title: "Factory Name",
      dataIndex: ["factory_info", "name"],
      key: "style",
    },
    {
      title: "Address",
      dataIndex: ["factory_info", "address"],
      key: "style",
    },
    {
      title: "Style",
      dataIndex: ["style_inquiry_map", "style_info", "style_no"],
      key: "style",
    },
    {
      title: "Exp. Order Qty.",
      dataIndex: "expected_order_quantity",
      key: "expected_order_quantity",
      render: (value, record) => {
        return <span>{commaSeparateNumber(value)}</span>;
      },
    },
    {
      title: "CM Value Status",
      dataIndex: "cm_status",
      key: "cm_status",
      render: (value, record) => {
        return <span>{value === 1 ? "Approved" : "Pending"}</span>;
      },
    },
    {
      title: "SAM Status",
      dataIndex: "operation_bulletin",
      key: "ob_status",
      render: (value) => {
        return <span>{value ? "Created" : "Pending"}</span>;
      },
    },
    {
      title: "",
      key: "action",
      width: "12%",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Space>
            {hasPermission([CM_VALUE_APPROVE_PERMISSION]) && activeTab !== "multi_style" && (
              <Button
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/merchandising/buyer-inquiry-approval/${record.id}${location.search}`)}
              >
                View Approval
              </Button>
            )}
            {/* {activeTab === "multi_style" &&
              hasPermission([BUYER_INQUIRY_MULTI_STYLE_CREATE_PERMISSION, BUYER_INQUIRY_CREATE_PERMISSION]) && (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openAddMultiStyleModal(record)}
                >
                  Add Multi Style
                </Button>
              )} */}
            <Dropdown overlay={menu(record)}>
              <MoreOutlined style={{ fontSize: "20px" }} />
            </Dropdown>
          </Space>
        </div>
      ),
    },
  ];

  const columns = useMemo(() => {
    if (activeTab === "multi_style") {
      return baseColumns.filter((col) => col?.title !== "Address" && col?.title !== "Style");
    }
    return baseColumns;
  }, [activeTab]);

  const deleteSelectedItem = async (id) => {
    const query = `${BUYER_INQUIRY_DELETE_ENDPOINT}/${id}`;
    const response = await deleteData(query);

    if (response) {
      HandleNotification("success", "bottomRight", "Buyer Inquiry Deleted Successfully!", null);
      getAllPermitList();
    } else {
      HandleNotification("error", "bottomRight", "Something Went Wrong!", null);
    }
  };

  const handleCsvUpload = async (file) => {
    if (!file) {
      HandleNotification("error", "bottomRight", "No File Selected", "Please select a CSV file.");
      return false;
    }

    const fileName = file?.name?.toLowerCase() || "";
    if (!fileName.endsWith(".csv")) {
      HandleNotification("error", "bottomRight", "Invalid File Format", "Please select a valid CSV (.csv) file.");
      return false;
    }

    setCsvUploading(true);
    const formData = new FormData();
    formData.append("csv_file", file);
    formData.append("file", file);

    try {
      const response = await postData(MIGRATE_BUYER_INQUIRY_ENDPOINT, formData);

      if (response && (response.code === 200 || response.status === "success")) {
        HandleNotification(
          "success",
          "bottomRight",
          "Upload Successful",
          response.message || "Buyer Inquiry imported successfully"
        );
        getAllPermitList();
      } else {
        const messageText = Array.isArray(response?.message)
          ? response.message.join(", ")
          : response?.message || "Failed to upload CSV file.";

        const errorData = response?.data;
        if (Array.isArray(errorData) && errorData.length > 0) {
          setUploadErrorModal({
            visible: true,
            message: messageText,
            errors: errorData,
          });
        } else {
          HandleNotification("error", "bottomRight", "Upload Failed", messageText);
        }
      }
    } catch (error) {
      HandleNotification(
        "error",
        "bottomRight",
        "Upload Failed",
        error?.message || "An unexpected error occurred during CSV upload."
      );
    } finally {
      setCsvUploading(false);
    }

    return false;
  };

  const closeMultiStyleCMModal = () => {
    setIsMultiStyleCMModalOpen(false);
    setSelectedMultiStyleInquiryId(null);
    setMultiStyleCMData([]);
    setMultiStyleCMOriginalData([]);
    setMultiStyleCMFilesList([]);
    setMultiStyleCMFieldsChanged(false);
  };

  const openAddMultiStyleModal = (record) => {
    setSelectedAddMultiStyleInquiry(record || null);
    setIsAddMultiStyleModalOpen(true);
  };

  const closeAddMultiStyleModal = () => {
    setIsAddMultiStyleModalOpen(false);
    setSelectedAddMultiStyleInquiry(null);
  };

  const checkMultiStyleCMFieldsChanged = (currentData, originalData) => {
    if (!Array.isArray(currentData) || !Array.isArray(originalData)) return false;

    const cmFields = ["expected_margin", "quoted_cm", "buyer_target", "negotiated_cm"];

    return currentData.some((row, idx) => {
      const originalRow = originalData[idx];
      if (!originalRow) return false;

      return cmFields.some(
        (field) =>
          Number(parseFloat(row?.[field] || 0).toFixed(2)) !==
          Number(parseFloat(originalRow?.[field] || 0).toFixed(2)),
      );
    });
  };

  const openMultiStyleCMModal = useCallback(async (inquiryId) => {
    if (!inquiryId) return;

    setSelectedMultiStyleInquiryId(inquiryId);
    setIsMultiStyleCMModalOpen(true);
    setIsMultiStyleCMModalLoading(true);
    setMultiStyleCMData([]);
    setMultiStyleCMOriginalData([]);
    setMultiStyleCMFilesList([]);
    setMultiStyleCMFieldsChanged(false);

    const query = `/api/buyer-inquiries/multi-style-inquiry-cm/${inquiryId}`;
    const response = await getData(query);

    if (response?.data?.code === 200) {
      const cmData = Array.isArray(response?.data?.data)
        ? response?.data?.data.map(enrichMultiStyleCMRow)
        : [];
      setMultiStyleCMData(cmData);
      setMultiStyleCMOriginalData(JSON.parse(JSON.stringify(cmData)));
    } else {
      HandleNotification("error", "bottomRight", response?.data?.message || "Failed to load CM data", null);
    }

    setIsMultiStyleCMModalLoading(false);
  }, []);

  const updateMultiStyleCMRow = (rowKey, field, value) => {
    setMultiStyleCMData((prev) => {
      const updatedData = prev.map((row, idx) => {
        if (idx !== rowKey) return row;

        const updatedRow = { ...row, [field]: value };

        if (field === "expected_margin") {
          const normalizedValue =
            value === 0 || value === null || value === undefined
              ? null
              : Math.trunc(Number(value));
          updatedRow.expected_margin = normalizedValue;
          updatedRow.total_expected_margin = calculateTotalExpectedMargin(
            updatedRow.breakeven_cm,
            normalizedValue,
          );
          updatedRow.quoted_cm = calculateQuotedCmFromTotalMargin(
            updatedRow.total_expected_margin,
          );
        }

        if (field === "negotiated_cm") {
          const differenceValue =
            value !== undefined && value !== null && updatedRow.breakeven_cm !== undefined && updatedRow.breakeven_cm !== null
              ? Number(value) - Number(updatedRow.breakeven_cm)
              : null;
          updatedRow.difference = differenceValue;
          updatedRow.operation_profit = differenceValue;
        }

        return updatedRow;
      });

      setMultiStyleCMFieldsChanged(checkMultiStyleCMFieldsChanged(updatedData, multiStyleCMOriginalData));
      return updatedData;
    });
  };

  const exportMultiStyleCMData = async () => {
    if (!selectedMultiStyleInquiryId) return;

    setIsMultiStyleCMExporting(true);
    const response = await getData(
      `/api/buyer-inquiries/multi-style-inquiry-cm/export/${selectedMultiStyleInquiryId}`,
      false,
      null,
      { "Content-Type": "blob" },
      "arraybuffer",
    );

    if (response?.status === 200) {
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Multi_style_CM_Data_${selectedMultiStyleInquiryId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } else {
      HandleNotification("error", "bottomRight", "Failed to export CM data", null);
    }

    setIsMultiStyleCMExporting(false);
  };

  const buildMultiStyleCMPayload = (row) => {
    const cmFile = multiStyleCMFilesList[0];
    const payload = {
      expected_margin: row?.expected_margin ?? null,
      total_expected_margin: row?.total_expected_margin ?? null,
      buyer_target: row?.buyer_target ?? null,
      negotiated_cm: row?.negotiated_cm ?? null,
      quoted_cm: row?.quoted_cm ?? null,
      difference: row?.difference ?? null,
      operation_profit: row?.operation_profit ?? null,
    };

    if (multiStyleCMFieldsChanged && cmFile?.id) {
      payload.cm_history_id = cmFile.id;
      payload.document_name = cmFile.document_name ?? cmFile.name ?? null;
      payload.document = cmFile.document ?? cmFile.path ?? null;
    } else {
      payload.cm_history_id = 0;
    }

    return payload;
  };

  const saveMultiStyleCMData = async () => {
    if (!multiStyleCMData?.length) return;

    if (!multiStyleCMFilesList[0]?.id && multiStyleCMFieldsChanged) {
      HandleNotification("error", "bottomRight", "Please upload CM document", null);
      return;
    }

    const inquiryIds = [...new Set(multiStyleCMData.map((row) => row.inquiry_id).filter(Boolean))];
    if (!inquiryIds.length) {
      HandleNotification("error", "bottomRight", "No inquiry found to update", null);
      return;
    }

    setIsMultiStyleCMSaving(true);
    let hasError = false;

    for (const inquiryId of inquiryIds) {
      const row = multiStyleCMData.find((item) => item.inquiry_id === inquiryId);
      const payload = buildMultiStyleCMPayload(row);

      const response = await postData(`/api/buyer-inquiries/multi-style-inquiry-cm/update/${inquiryId}`, payload);
      if (!response || response?.code !== 200) {
        hasError = true;
      }
    }

    if (!hasError) {
      HandleNotification("success", "bottomRight", "CM data updated successfully", null);
      await openMultiStyleCMModal(selectedMultiStyleInquiryId);
    }

    setIsMultiStyleCMSaving(false);
  };

  const renderEditableCMNumber = (field, row, rowKey, options = {}) => (
    <InputNumber
      size="small"
      style={{ width: "100%" }}
      min={options.min}
      precision={options.precision}
      value={row?.[field]}
      disabled={!hasPermission([BUYER_INQUIRY_EDIT_PERMISSION])}
      onChange={(value) => updateMultiStyleCMRow(rowKey, field, value)}
    />
  );

  const multiStyleCMColumns = useMemo(
    () => [
      { title: "Style No", dataIndex: "style_no", key: "style_no", width: 110, fixed: "left" },
      { title: "Breakeven CM", dataIndex: "breakeven_cm", key: "breakeven_cm", width: 110 },
      { title: "Total SAM", dataIndex: "standard_minute", key: "standard_minute", width: 90 },
      { title: "Number of Workers", dataIndex: "manpower_per_line", key: "manpower_per_line", width: 120 },
      { title: "Hours per Day", dataIndex: "working_hour_per_day", key: "working_hour_per_day", width: 110 },
      { title: "Target (pc) per Hour", dataIndex: "production_per_hours", key: "production_per_hours", width: 130 },
      { title: "Required Efficiency", dataIndex: "factory_efficiency", key: "factory_efficiency", width: 130 },
      {
        title: "Expected Profit %",
        dataIndex: "expected_margin",
        key: "expected_margin",
        width: 130,
        render: (_, row, rowKey) => renderEditableCMNumber("expected_margin", row, rowKey, { precision: 0 }),
      },
      {
        title: "Total Expected Margin $",
        dataIndex: "total_expected_margin",
        key: "total_expected_margin",
        width: 150,
        render: (value) => toFixedAndComma(value),
      },
      {
        title: "Quoted CM",
        dataIndex: "quoted_cm",
        key: "quoted_cm",
        width: 110,
        render: (_, row, rowKey) => renderEditableCMNumber("quoted_cm", row, rowKey, { precision: 2 }),
      },
      {
        title: "Buyer Target $",
        dataIndex: "buyer_target",
        key: "buyer_target",
        width: 120,
        render: (_, row, rowKey) => renderEditableCMNumber("buyer_target", row, rowKey, { precision: 2 }),
      },
      {
        title: "Negotiated CM",
        dataIndex: "negotiated_cm",
        key: "negotiated_cm",
        width: 120,
        render: (_, row, rowKey) => renderEditableCMNumber("negotiated_cm", row, rowKey, { precision: 2 }),
      },
      {
        title: "Difference $",
        dataIndex: "difference",
        key: "difference",
        width: 110,
        render: (value) => commaSeparateNumber(value),
      },
      {
        title: "Operational Profit $",
        dataIndex: "operation_profit",
        key: "operation_profit",
        width: 140,
        render: (value) => commaSeparateNumber(value),
      },
    ],
    [multiStyleCMData],
  );

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/merchandising/view-buyer-inquiry/${data.id}`} style={{ display: "block", width: "100%" }}>
              <EyeOutlined /> View
            </Link>
          ),
          key: 1,
          disabled: !hasPermission([BUYER_INQUIRY_VIEW_PERMISSION]),
        },
        {
          label: (
            <Link to={`/merchandising/update-buyer-inquiry/${data.id}`} style={{ display: "block", width: "100%" }}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: 2,
          disabled: !hasPermission([BUYER_INQUIRY_EDIT_PERMISSION]),
        },
        {
          label: (
            <Popconfirm
              title="Are you sure you want to delete?"
              onConfirm={() => {
                deleteSelectedItem(data?.id);
              }}
            >
              <span
                style={{
                  color: "red",
                  display: "block",
                  width: "100%",
                }}
              >
                <DeleteOutlined /> Delete
              </span>
            </Popconfirm>
          ),
          key: 3,
          disabled: !hasPermission([BUYER_INQUIRY_DELETE_PERMISSION]),
        },
        {
          label: (
            <Link
              to={`/merchandising/buyer-inquiry/special-process/${data.id}`}
              style={{ display: "block", width: "100%" }}
            >
              <EditOutlined /> Special Process
            </Link>
          ),
          key: 4,
          disabled: !hasPermission([BUYER_INQUIRY_EDIT_PERMISSION]),
        },
        // {
        //   label: (
        //     <Link
        //       to={`/merchandising/buyer-inquiry-approval/${data.id}`}
        //       style={{ display: "block", width: "100%" }}
        //     >
        //       <EyeOutlined /> View Approval
        //     </Link>
        //   ),
        //   key: "view_approval",
        //   disabled: !hasPermission([CM_VALUE_APPROVE_PERMISSION]),
        // },
        ...(activeTab === "multi_style"
          ? [
            //     {
            //       label: (
            //         <span style={{ display: "block", width: "100%" }}>
            //           <PlusOutlined /> Add Multi Style
            //         </span>
            //       ),
            //       key: "add_multi_style",
            //       onClick: () => openAddMultiStyleModal(data),
            //       disabled: !hasPermission([
            //         BUYER_INQUIRY_MULTI_STYLE_CREATE_PERMISSION,
            //         BUYER_INQUIRY_CREATE_PERMISSION,
            //       ]),
            //     },
            {
              label: (
                <span style={{ display: "block", width: "100%" }}>
                  <EyeOutlined /> CM View
                </span>
              ),
              key: "cm_view",
              onClick: () => openMultiStyleCMModal(data?.id),
              disabled: !hasPermission([BUYER_INQUIRY_VIEW_PERMISSION]),
            },
          ]
          : []),
      ]}
    />
  );

  const paginate = (page, pageSize) => {
    const updatedFilters = {
      ...(tabWiseFilterValues?.[activeTab] || {}),
      page,
      per_page: pageSize || tabWiseRangeData?.[activeTab] || 10,
    };

    getAllPermitList(updatedFilters, activeTab);
  };

  const onTabChange = (tabKey) => {
    setActiveTab(tabKey);
    getAllPermitList(undefined, tabKey);
  };

  const extra = (
    <Space>
      <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button>
      <Upload
        accept=".csv"
        showUploadList={false}
        beforeUpload={handleCsvUpload}
      >
        <Button
          key="upload-csv"
          size="small"
          type="primary"
          ghost
          icon={<UploadOutlined />}
          loading={csvUploading}
          disabled={!hasPermission([CM_VALUE_APPROVE_PERMISSION])}
        >
          Upload CSV
        </Button>
      </Upload>
      <Button key="3" size="small" type="primary" disabled={!hasPermission([BUYER_INQUIRY_CREATE_PERMISSION])}>
        <Link to="/merchandising/add-buyer-inquiry">
          {hasPermission([BUYER_INQUIRY_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
        </Link>
      </Button>
    </Space>
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={permitList}
        fetchTableData={getAllPermitList}
        filterName="buyer_inquiry"
      />
    ),
    [activeTab, getAllPermitList, permitList, showFilter],
  );

  const tableSection = (
    <>
      <Space style={{ marginBottom: 8 }}>
        <span>Received Date:</span>
        <DatePicker.RangePicker size="small" value={dateRange} onChange={handleDateRangeChange} allowClear={false} />
      </Space>
      {memoizedCustomFilter}
      <ListTable
        loading={loading}
        dataSource={permitList}
        columns={columns}
        locale={{
          emptyText: (
            <Empty
              description={`No records between ${dateRange?.[0]?.format("DD-MMM-YYYY")} and ${dateRange?.[1]?.format(
                "DD-MMM-YYYY",
              )}. Widen the range above.`}
            />
          ),
        }}
        pagination={{
          showSizeChanger: false,
          current: tabWiseCurrentPage?.[activeTab] || 1,
          total: tabWiseTotalData?.[activeTab] || 0,
          pageSize: tabWiseRangeData?.[activeTab] || 10,
          onChange: (page, pageSize) => paginate(page, pageSize),
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </>
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header title="Buyer Inquiry" subTitle="" actions={extra} />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={onTabChange}
            items={[
              {
                key: "single_style",
                label: "Single Style",
                children: tableSection,
              },
              {
                key: "multi_style",
                label: "Multi Style",
                children: tableSection,
              },
            ]}
          />
        </Card>
      </div>

      <Modal
        open={isMultiStyleCMModalOpen}
        title="CM Data For Multi Style"
        onCancel={closeMultiStyleCMModal}
        width={1500}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={
          <Space>
            <Button onClick={closeMultiStyleCMModal}>Close</Button>
            <Button
              icon={<FileExcelOutlined />}
              loading={isMultiStyleCMExporting}
              onClick={exportMultiStyleCMData}
              disabled={!hasPermission([BUYER_INQUIRY_VIEW_PERMISSION])}
            >
              Excel Export
            </Button>
            <Button
              type="primary"
              loading={isMultiStyleCMSaving}
              onClick={saveMultiStyleCMData}
              disabled={!hasPermission([BUYER_INQUIRY_EDIT_PERMISSION])}
            >
              Save
            </Button>
          </Space>
        }
      >
        <CompactHorizontalScroll
          className="compact-h-scroll--modal"
          contentKey={`${multiStyleCMData?.length}-${isMultiStyleCMModalLoading}`}
        >
          <ListTable
            bordered
            size="small"
            loading={isMultiStyleCMModalLoading}
            dataSource={multiStyleCMData?.map((item, idx) => ({ ...item, key: idx })) || []}
            pagination={false}
            scroll={{ x: 1600 }}
            columns={multiStyleCMColumns}
          />
        </CompactHorizontalScroll>
        <UploadCMDoc
          DOC_ADD="/api/buyer-inquiries/store-cm-histories"
          setFileList={setMultiStyleCMFilesList}
          filesList={multiStyleCMFilesList}
          view={!hasPermission([BUYER_INQUIRY_EDIT_PERMISSION])}
          showUploadRequired={
            hasPermission([BUYER_INQUIRY_EDIT_PERMISSION]) &&
            multiStyleCMFieldsChanged &&
            !(Array.isArray(multiStyleCMFilesList) && multiStyleCMFilesList.length > 0 && multiStyleCMFilesList[0]?.id)
          }
        />
      </Modal>

      <AddMultiStyleModal
        open={isAddMultiStyleModalOpen}
        inquiry={selectedAddMultiStyleInquiry}
        onCancel={closeAddMultiStyleModal}
        onSuccess={() => getAllPermitList(undefined, "multi_style")}
      />

      <Modal
        open={uploadErrorModal.visible}
        title="CSV Upload Error Details"
        onCancel={() => setUploadErrorModal({ visible: false, message: "", errors: [] })}
        width={900}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setUploadErrorModal({ visible: false, message: "", errors: [] })}
          >
            Close
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: "#ff4d4f", fontWeight: 500, fontSize: 14, marginBottom: 6 }}>
            {uploadErrorModal.message}
          </p>
          <p style={{ color: "#666", marginBottom: 0 }}>
            The following row(s) in the CSV file could not be processed due to missing or invalid data:
          </p>
        </div>
        <Table
          bordered
          size="small"
          dataSource={uploadErrorModal.errors.map((item, idx) => ({ ...item, key: idx }))}
          pagination={{ pageSize: 5 }}
          scroll={{ x: 750 }}
          columns={[
            {
              title: "Row",
              dataIndex: "Row",
              key: "Row",
              width: 70,
              render: (text) => <strong>{text}</strong>,
            },
            {
              title: "Buying Office",
              dataIndex: "Buying Office",
              key: "Buying Office",
              width: 120,
            },
            {
              title: "Buyer",
              dataIndex: "Buyer",
              key: "Buyer",
              width: 130,
              render: (text, record) => (
                <span style={{ color: !record.buyer ? "#ff4d4f" : "inherit" }}>
                  {text || "—"} {!record.buyer && <small>(Not Found)</small>}
                </span>
              ),
            },
            {
              title: "Sub Contract Factory",
              dataIndex: "Sub Contract Factory",
              key: "Sub Contract Factory",
              width: 150,
              render: (text, record) => (
                <span style={{ color: !record.subContractFactory && !record.buyer ? "#ff4d4f" : "inherit" }}>
                  {text || "—"}
                </span>
              ),
            },
            {
              title: "Received By",
              dataIndex: "Received By",
              key: "Received By",
              width: 140,
              render: (text, record) => (
                <span style={{ color: !record.receivedBy ? "#ff4d4f" : "inherit" }}>
                  {text || "—"} {!record.receivedBy && <small>(Not Found)</small>}
                </span>
              ),
            },
            {
              title: "Reference",
              dataIndex: "Reference",
              key: "Reference",
              width: 130,
              render: (text, record) => (
                <span style={{ color: !record.reference ? "#ff4d4f" : "inherit" }}>
                  {text || "—"} {!record.reference && <small>(Not Found)</small>}
                </span>
              ),
            },
            {
              title: "Brand",
              dataIndex: "Brand",
              key: "Brand",
              width: 110,
            },
          ]}
        />
      </Modal>
    </Fragment>
  );
};

export default BuyerInquiry;
